/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The two surfaces the `Shared…` chip opens, rendered once** — `X30c` (`#1303`).
 *
 * A companion to {@link useSharedExpressionSlot}: that owns *which one is open*, this owns *what
 * they look like*. Both exist because three components had a copy of each, and the copies had
 * already drifted — a set chip opened the picker on all of them, which is the `X30a` defect
 * surviving everywhere the fix did not reach.
 *
 * **Placed by the caller, not by this component.** A row puts it among its zones and a branch inside
 * its coloured box, which is a genuine per-surface difference; what must not differ is what opens,
 * when, and what it can do.
 */

import React from 'react';
import { SharedExpressionPicker } from './SharedExpressionPicker';
import { SharedExpressionPanel } from './SharedExpressionPanel';
import { useExpressionLibraryAccess } from './expression-library-access';
import type { SharedExpressionSlot } from './useSharedExpressionSlot';
import type { FieldSuggestionsFn } from './DecoratorExpressionEditor';
import type { MacroExpression } from '../../../types/decorator-expression';

export const SharedExpressionSurfaces: React.FC<{
  slot: SharedExpressionSlot;
  onChange: (next: MacroExpression) => void;
  /** This surface's path vocabulary, so a response mapping suggests `response.*` inside the panel. */
  getFieldSuggestions?: FieldSuggestionsFn;
}> = ({ slot, onChange, getFieldSuggestions }) => {
  const { offersLibrary, resolvesAgainst } = useExpressionLibraryAccess();

  {
    /* **Picked where the value is** (`X28a`) — choosing replaces this value, so what it replaces
       should still be on screen. */
  }
  if (slot.pickerOpen)
    return (
      <SharedExpressionPicker
        capability={offersLibrary}
        resolvesAgainst={resolvesAgainst}
        onPick={(picked) => {
          slot.closePicker();
          onChange(picked);
        }}
        onCancel={slot.closePicker}
      />
    );

  if (slot.panelOpen && slot.reference)
    return (
      <SharedExpressionPanel
        reference={slot.reference}
        onChange={onChange}
        onClose={slot.closePanel}
        onPickAnother={slot.pickAnother}
        offersLibrary={offersLibrary}
        resolvesAgainst={resolvesAgainst}
        getFieldSuggestions={getFieldSuggestions}
      />
    );

  return null;
};
